import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

// A line that is only `**Title**` is a section header in our legal docs → promote to a real heading.
const promoteHeaders = (text) =>
  text.replace(/^\*\*(.+?)\*\*[ \t]*$/gm, (_m, title) => `\n### ${title}\n`);

/**
 * Renders the markdown stored in legal documents / onboarding agreements.
 * Falls back to plain paragraphs when the text has no markdown at all.
 */
const MarkdownContent = ({ content, tone = 'dark-ink', className = '' }) => {
  if (!content) return null;

  const ink = tone === 'light-ink' ? 'text-gray-100' : 'text-black';
  const heading = tone === 'light-ink' ? 'text-white' : 'text-[#331a67]';
  const link = tone === 'light-ink' ? 'text-cyan-300 hover:text-cyan-200' : 'text-cyan-700 hover:text-cyan-900';
  // Global CSS styles h1-h4 with a serif small-caps face; legal copy reads better in the body face.
  const headingStyle = { fontFamily: 'inherit', fontVariant: 'normal', textTransform: 'none', letterSpacing: 'normal' };

  return (
    <div className={`${ink} leading-relaxed space-y-3 ${className}`} data-testid="markdown-content">
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          h1: ({ children }) => <h2 style={headingStyle} className={`text-lg font-bold mt-7 mb-2 ${heading}`}>{children}</h2>,
          h2: ({ children }) => <h3 style={headingStyle} className={`text-lg font-bold mt-6 mb-2 ${heading}`}>{children}</h3>,
          h3: ({ children }) => <h4 style={headingStyle} className={`text-base font-bold mt-6 mb-2 ${heading}`}>{children}</h4>,
          p: ({ children }) => <p className="mb-3">{children}</p>,
          strong: ({ children }) => <strong className={`font-semibold ${heading}`}>{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className={`underline ${link}`}>
              {children}
            </a>
          ),
          hr: () => <hr className="my-6 border-gray-300" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-cyan-500/50 pl-4 italic my-3">{children}</blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full text-sm border-collapse">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{children}</th>,
          td: ({ children }) => <td className="border border-gray-300 px-3 py-2">{children}</td>,
          code: ({ children }) => <code className="px-1 py-0.5 rounded bg-black/10 text-sm">{children}</code>,
        }}
      >
        {promoteHeaders(content)}
      </Markdown>
    </div>
  );
};

export default MarkdownContent;
